'use client'

/**
 * One feedback marker on the pin overlay (Issue #13578).
 *
 * A teardrop at the top-right of the element the feedback points at, tinted
 * by status, carrying either its reply count or — when several threads
 * collapsed into it — the number of threads behind it. Clicking it opens
 * `FeedbackPinPopover`.
 *
 * ## It is a real `<button>`, and that is load-bearing
 *
 * The overlay is a `position: fixed` portal layer, so a styled `<div>` here
 * would be unreachable by Tab and announce as nothing. `golden-path-e2e` is
 * written to catch exactly that and is NOT enforced anywhere yet, so the
 * requirement is held by `pin-overlay.test.tsx`'s keyboard-reachability cases
 * instead. `PopoverTrigger asChild` keeps the semantics: Enter and Space
 * open it, Escape closes it, and focus returns to the marker.
 *
 * ## sai-ui `Popover` (#15028)
 *
 * Through sai-ui's `Popover` since #15028 (phase 4 of #14954), not
 * `@radix-ui/react-popover` directly. The package's content portals itself
 * and paints its own surface, border, radius and shadow, so the `Card` that
 * used to supply them is gone and the classes left here are the layer, the
 * width and the padding. It exports no arrow, so the panel has none — the
 * #13788 arrow went with the direct import. The dialog the content renders
 * is named "Feedback thread", where it used to be announced unnamed.
 *
 * ## Touch target versus cluster radius
 *
 * The hit area is 44x44 (an unconditional `min-h-11 min-w-11`) per the
 * project's touch-target rule, while the visible teardrop is 28px and the cluster radius is the 24px
 * the feature specifies. Two markers 30px apart therefore have overlapping
 * hit areas without being clustered. That tension is accepted rather than
 * resolved by shrinking the target: the overlapping band is a few pixels at
 * the edge of two targets, the topmost of which is still activatable, whereas
 * a sub-44px target fails for every touch user on every pin.
 */

import { useEffect, useRef, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@sai/ui/components'
import { MessageCircle } from '@/components/icons'
import { cn } from '@/lib/utils'
import { getPinTint } from '@/components/feedback/feedback-status-utils'
import { FEEDBACK_MODE_EXEMPT_ATTRIBUTE } from '@/components/feedback/feedback-mode-attributes'
import type { PinCluster } from '@/components/feedback/pin-cluster'
import { FeedbackPinPopover } from '@/components/feedback/feedback-pin-popover'

export interface FeedbackPinProps {
  cluster: PinCluster
  isAdmin: boolean
  timezone: string
  /** Fired when the popover opens, with the number of threads behind it. */
  onOpened?: (feedbackId: string, clusterSize: number) => void
  onTriaged?: () => void
  /**
   * Published on every open and close, and once more with `false` on unmount
   * (#13788).
   *
   * The picker suspends for as long as this is true, which is what makes
   * "open a thread while armed" cost no decision. The UNMOUNT call is the
   * load-bearing one: leaving the mode unmounts the whole overlay, Radix
   * fires no close for that, and a latched `true` would suspend the picker
   * for the rest of the session (edge case 5).
   */
  onThreadOpenChange?: (open: boolean) => void
  /**
   * A `?feedback=<id>` landing naming a thread (#13788). When this cluster
   * holds it, the popover opens itself — once.
   */
  autoOpenFeedbackId?: string | null
  /** Called the one time {@link autoOpenFeedbackId} opened this pin. */
  onDeepLinkOpened?: (feedbackId: string) => void
}

/**
 * The marker's accessible name.
 *
 * Spelled out rather than assembled from the visible glyph, because the
 * visible content is a number and an icon — "3" announces nothing. The reply
 * count is included because it is the one piece of information that decides
 * whether the pin is worth opening.
 */
function pinLabel(cluster: PinCluster): string {
  if (cluster.pins.length > 1) {
    // "here", not "on this element" (Issue #13652). Clustering groups by
    // PROXIMITY of the drawn coordinate, and since the viewport clamp two
    // pins on two different elements can be bounded onto the same point and
    // merged — so claiming a shared element tells a screen-reader user
    // something that is not true.
    return `${cluster.pins.length} feedback threads here`
  }
  const pin = cluster.pins[0]
  const { replyCount, status } = pin.anchor
  const readableStatus = status.replace(/_/g, ' ').toLowerCase()
  const replies = replyCount === 1 ? '1 reply' : `${replyCount} replies`
  // The "moved" badge is rendered visually below; without this a screen-reader
  // user gets no equivalent of it, and the pin's position is the one thing
  // they cannot verify for themselves.
  const moved = pin.resolved ? '' : ', anchor moved'
  return `Feedback thread (${readableStatus}), ${replies}${moved}`
}

export function FeedbackPin({
  cluster,
  isAdmin,
  timezone,
  onOpened,
  onTriaged,
  onThreadOpenChange,
  autoOpenFeedbackId,
  onDeepLinkOpened,
}: FeedbackPinProps) {
  /**
   * CONTROLLED since #13788, for two reasons that both need the state here:
   * the deep link has to be able to open a specific thread, and the picker
   * has to be told when one is up. An uncontrolled `Popover` can do
   * neither.
   */
  const [open, setOpen] = useState(false)
  /** The deep link fires once per landing, never once per re-measure. */
  const deepLinkConsumedRef = useRef(false)
  const deepLinkTarget =
    autoOpenFeedbackId != null &&
    cluster.pins.some(p => p.anchor.feedbackId === autoOpenFeedbackId)
      ? autoOpenFeedbackId
      : null

  useEffect(() => {
    if (deepLinkTarget === null || deepLinkConsumedRef.current) return
    deepLinkConsumedRef.current = true
    setOpen(true)
    onDeepLinkOpened?.(deepLinkTarget)
    // `onDeepLinkOpened` is deliberately not a dependency: the overlay
    // re-renders on every scroll frame, and an unstable callback identity
    // would re-run this and fight a viewer trying to close the panel. The ref
    // above makes a re-run harmless anyway; this keeps it from happening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkTarget])

  /**
   * The unmount half of `onThreadOpenChange`.
   *
   * A ref, not `open` in the dependency list: a cleanup that read `open`
   * would fire a spurious `false` on every open→close transition as well,
   * which is harmless but makes the published stream lie about how many
   * threads were opened. What this must cover is exactly the case Radix
   * cannot report — the tree going away underneath an open panel.
   */
  const openRef = useRef(false)
  openRef.current = open
  useEffect(() => {
    return () => {
      if (openRef.current) onThreadOpenChange?.(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- unmount only
  }, [])

  const primary = cluster.pins[0]
  const tint = getPinTint(primary.anchor.status)
  const count =
    cluster.pins.length > 1 ? cluster.pins.length : primary.anchor.replyCount
  const hasMoved = cluster.pins.some(p => !p.resolved)

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next)
        onThreadOpenChange?.(next)
        if (next) onOpened?.(primary.anchor.feedbackId, cluster.pins.length)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`feedback-pin-${cluster.id}`}
          aria-label={pinLabel(cluster)}
          style={{ top: `${cluster.y}px`, left: `${cluster.x}px` }}
          className={cn(
            // Positioned from the element's top-right corner, pulled half a
            // marker left and a full marker up so the teardrop's point lands
            // ON the corner rather than beside it.
            'absolute -translate-x-1/2 -translate-y-full',
            'pointer-events-auto flex items-center justify-center',
            // eslint-disable-next-line cadence-design-system/touch-target-on-coarse-pointer -- Unconditional: PIN_MARKER_SIZE_PX clamps on this 44px box.
            'min-h-11 min-w-11',
            'focus-visible:outline-none focus-visible:ring-2',
            // `dark:ring-offset-surface-elevated` is inseparable from the
            // non-zero offset: Tailwind initialises `--tw-ring-offset-color`
            // to `#fff`, so the offset alone paints a white band around a
            // focused marker in dark mode. PATTERN_REFERENCE.md documents
            // this as the one tokenized colour that keeps its `dark:` prefix.
            'focus-visible:ring-stroke-selected focus-visible:ring-offset-2',
            'dark:ring-offset-surface-elevated',
          )}
        >
          <span
            className={cn(
              // Teardrop: a circle with one square corner, rotated so the
              // square corner points down at the element.
              'flex h-7 min-w-7 items-center justify-center border',
              'rounded-full rounded-br-none rotate-45 shadow-sm',
              tint.marker,
            )}
          >
            <span
              className={cn(
                '-rotate-45 text-xs font-semibold leading-none',
                tint.label,
              )}
            >
              {count > 0 ? (
                count
              ) : (
                <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
              )}
            </span>
          </span>
          {hasMoved && (
            <span
              className={cn(
                // `bottom-0`, not `-bottom-1` (Issue #13652). The clamp
                // bounds the 44x44 BUTTON, so anything painted outside it is
                // still clipped at a viewport edge — and the fallback pin
                // this badge belongs to is exactly the class the clamp's
                // fallback branch is about. The teardrop is 28px inside a
                // 44px box, so sitting the badge on the box's bottom edge
                // moves it 4px without overlapping the marker.
                'absolute bottom-0 left-1/2 -translate-x-1/2',
                'rounded px-1 text-xs font-medium leading-tight',
                'bg-surface-elevated text-text-secondary border border-stroke-extra-light',
              )}
            >
              moved
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={8}
        collisionPadding={16}
        aria-label="Feedback thread"
        /*
          #13788 — the defect this issue is named for.

          `z-50` put the panel UNDER the picker's `fixed inset-0` capture
          layer (then `z-400`): the pin opened (the marker layer was
          `z-410`) and every click aimed at the reply textarea landed on the
          picker, which previewed the element behind the panel instead. Since the
          only way to SEE a pin was to enter the placing tool, there was no
          state in the product in which a person could answer a comment.

          `z-feedback-popover` (#16841, on sai-ui's layer scale since
          #16990) is above the capture layer (`z-feedback-picker`), the
          marker layer (`z-feedback-pins`) and the banner
          (`z-feedback-banner`), and below `FeedbackModal`'s
          `z-feedback-modal` so a submit still opens over the thread rather
          than under it. The whole feedback band sits above ordinary modals'
          `z-modal` and every `z-menu`, so a thread anchored inside a dialog
          opens over it. IMPORTANT (`!`), because the package's content
          carries its own `z-modal`, which `cn` does not merge away (a
          custom layer name is not a z-index class to tailwind-merge) and
          which sorts AFTER `z-feedback-*` in the build, so without `!` the
          thread would paint at 50, under the picker.

          `data-feedback-mode-exempt` is the other half: the picker
          disables its own pointer events for one `elementFromPoint` call,
          so without the opt-out the element under the cursor while
          somebody types a reply IS the reply box, and their next pick
          anchors feedback to the panel.

          `w-auto`: the thread sizes itself (`FeedbackPinPopover` is
          `w-[28rem] max-w-[90vw]`), over the package's fixed `w-72`.
        */
        {...{ [FEEDBACK_MODE_EXEMPT_ATTRIBUTE]: 'true' }}
        className="z-feedback-popover! w-auto overflow-hidden p-4"
      >
        <FeedbackPinPopover
          pins={cluster.pins}
          isAdmin={isAdmin}
          timezone={timezone}
          onTriaged={onTriaged}
        />
      </PopoverContent>
    </Popover>
  )
}
